import React, { useState, useEffect, useRef } from 'react';
import { serverPHP } from '../const/consts';
import { postJSON, NetworkError, getAdaptiveTimeout } from '../composants/NetworkHandler';

const MAX_CHAR_LIMIT = 150;

const Redaction = ({
  user,
  setUser,
  phase,
  sujet,
  goNext,
  setModalContent,
  setModalTitle,
  setShowModal,
  parametres,
  type,
  redactionLongue
}) => {
  const [displayedContribution, setDisplayedContribution] = useState('');
  const [contribution, setContribution] = useState('');
  const [charCount, setCharCount] = useState(MAX_CHAR_LIMIT);
  const [saveError, setSaveError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 2;
  const inputRef = useRef(null);
  const isSubmitting = useRef(false); // Pour éviter les double-clics

  useEffect(() => {
    setDisplayedContribution('');
    fetchCurrentContribution();
  }, [phase, user.currentsession, user.id, setUser]);

  const fetchCurrentContribution = async () => {
    setSaveError(null);
    
    try {
      const timeout = getAdaptiveTimeout(8000);
      
      const data = await postJSON(
        `${serverPHP}getContributions.php`,
        {
          session_id: user.currentsession,
          user_id: user.id,
          phase: phase,
          action: 'redaction'
        },
        { timeout, retries: 1 }
      );

      if (data.success) {
        if (data.current_contribution) {
          setDisplayedContribution(data.current_contribution.contribution);
          setUser(prevUser => ({ ...prevUser, isready: true }));
        } else {
          setUser(prevUser => ({ ...prevUser, isready: false }));
        }
        setRetryCount(0);
      } else {
        throw new NetworkError(
          'SERVER_ERROR',
          'Erreur lors de la récupération de la contribution',
          data.message || 'Réponse serveur invalide'
        );
      }
    } catch (error) {
      console.error('Failed to fetch contributions:', error);
      
      if (error instanceof NetworkError) {
        if (error.type === 'NETWORK_ERROR' || error.type === 'TIMEOUT') {
          setSaveError({
            type: 'FETCH_WARNING',
            message: 'Impossible de vérifier votre contribution existante',
            details: 'Vous pouvez continuer à rédiger, mais votre contribution précédente pourrait ne pas s\'afficher.'
          });
        }
      }
      
      setUser(prevUser => ({ ...prevUser, isready: false }));
    }
  };

  const handleSendContribution = async () => {
    if (!contribution.trim() || isSubmitting.current) return;
    
    isSubmitting.current = true;
    setSaveError(null);
    
    if (!redactionLongue && contribution.length > MAX_CHAR_LIMIT) {
      setSaveError({
        type: 'VALIDATION_ERROR',
        message: 'Contribution trop longue',
        details: `Votre contribution dépasse la limite de ${MAX_CHAR_LIMIT} caractères.`
      });
      isSubmitting.current = false;
      return;
    }

    if (contribution.trim().length < 5) {
      setSaveError({
        type: 'VALIDATION_ERROR',
        message: 'Contribution trop courte',
        details: 'Votre contribution doit contenir au moins 5 caractères.'
      });
      isSubmitting.current = false;
      return;
    }
    
    const previousContribution = displayedContribution;
    const previousReady = user.isready;
    
    setDisplayedContribution(contribution);
    setContribution('');
    setCharCount(MAX_CHAR_LIMIT);
    setUser(prevUser => ({ ...prevUser, isready: true }));
    
    try {
      const timeout = getAdaptiveTimeout(10000);
      
      const data = await postJSON(
        `${serverPHP}addContribution.php`,
        {
          session_id: user.currentsession,
          user_id: user.id,
          contribution: contribution.trim(),
          phase: phase
        },
        { timeout, retries: 1 }
      );
      
      if (data.success) {
        setRetryCount(0);
        if (inputRef.current) {
          inputRef.current.focus();
        }
      } else {
        throw new NetworkError(
          'SERVER_ERROR',
          'Erreur lors de la sauvegarde',
          data.error || 'La contribution n\'a pas pu être sauvegardée'
        );
      }
    } catch (error) {
      console.error('Error sending contribution:', error);
      
      setDisplayedContribution(previousContribution);
      setContribution(contribution);
      setUser(prevUser => ({ ...prevUser, isready: previousReady }));
      
      let errorMessage = 'Erreur lors de l\'envoi de la contribution';
      let errorDetails = 'Veuillez réessayer.';
      
      if (error instanceof NetworkError) {
        switch (error.type) {
          case 'TIMEOUT': errorMessage = 'L\'envoi prend trop de temps'; errorDetails = 'Le serveur met plus de temps que prévu à répondre. Votre texte a été conservé.'; break;
          case 'NETWORK_ERROR': errorMessage = 'Problème de connexion'; errorDetails = 'Vérifiez votre connexion internet. Votre texte a été conservé.'; break;
          case 'SERVER_ERROR': errorMessage = 'Erreur du serveur'; errorDetails = error.details || 'Le serveur ne peut pas traiter votre contribution actuellement.'; break;
        }
      }
      
      setSaveError({
        type: error.type || 'UNKNOWN_ERROR',
        message: errorMessage,
        details: errorDetails
      });
    } finally {
      isSubmitting.current = false;
    }
  };

  const handleCancelContribution = async () => {
    if (isSubmitting.current) return;
    
    isSubmitting.current = true;
    setSaveError(null);
    
    const previousContribution = displayedContribution;
    setDisplayedContribution('');
    setUser(prevUser => ({ ...prevUser, isready: false }));
    
    try {
      const data = await postJSON(
        `${serverPHP}addContribution.php`,
        {
          session_id: user.currentsession,
          user_id: user.id,
          contribution: '',
          phase: phase
        },
        { timeout: 8000, retries: 1 }
      );
      
      if (!data.success) {
        throw new NetworkError('SERVER_ERROR', 'Erreur lors de la suppression', data.error);
      }
      setRetryCount(0);
    } catch (error) {
      console.error('Error canceling contribution:', error);
      
      setDisplayedContribution(previousContribution);
      setUser(prevUser => ({ ...prevUser, isready: true }));
      
      setSaveError({
        type: 'SERVER_ERROR',
        message: 'Impossible de supprimer la contribution',
        details: 'Veuillez réessayer ou actualiser la page.'
      });
    } finally {
      isSubmitting.current = false;
    }
  };

  const handleRetry = () => {
    if (retryCount < maxRetries) {
      setRetryCount(prev => prev + 1);
      setSaveError(null);
      if (contribution.trim()) {
        handleSendContribution();
      } else {
        fetchCurrentContribution();
      }
    }
  };

  const showErrorModal = () => {
    if (!saveError) return;
    
    const actions = [];
    if (retryCount < maxRetries && saveError.type !== 'VALIDATION_ERROR') {
      actions.push(<button key="retry" className="btn btn-primary" onClick={() => { handleRetry(); setShowModal(false); }}>🔄 Réessayer ({retryCount + 1}/{maxRetries + 1})</button>);
    }
    actions.push(<button key="close" className="btn btn-secondary" onClick={() => { setSaveError(null); setShowModal(false); }}>Fermer</button>);

    setModalContent(
      <div className="text-center">
        <div className="text-4xl mb-3">{saveError.type === 'VALIDATION_ERROR' ? '⚠️' : '❌'}</div>
        <h3 className="font-semibold mb-3">{saveError.message}</h3>
        <p className="text-sm text-secondary mb-4">{saveError.details}</p>
        <div className="flex gap-2 justify-center">{actions}</div>
      </div>
    );
    setModalTitle("Erreur");
    setShowModal(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !redactionLongue && !e.shiftKey) {
      e.preventDefault();
      handleSendContribution();
    }
  };

  const handleChange = (e) => {
    const input = e.target.value;
    setContribution(input);
    if (!redactionLongue && input.length <= MAX_CHAR_LIMIT) {
      setCharCount(MAX_CHAR_LIMIT - input.length);
    }
  };

  const getMessageInfo = (article, statut, icon) => {
    return (
      <div className="card text-center">
        <div className="text-4xl mb-3">{icon}</div>
        <h3 className="font-semibold mb-3">Accès restreint</h3>
        <p>Vous avez le statut {article}<span className="font-semibold text-primary">{statut}</span>.<br />Vous ne pouvez donc pas participer aux phases de rédaction.</p>
        <p className="text-sm text-secondary mt-3">Pour changer votre statut, cliquez sur votre nom en haut à gauche.</p>
      </div>
    );
  };

  useEffect(() => {
    if (saveError && saveError.type !== 'FETCH_WARNING') {
      showErrorModal();
    }
  }, [saveError]);

  if (user.role === 1) return getMessageInfo("d'", 'évaluateur', '⭐');
  if (user.role === 2) return getMessageInfo('de ', 'spectateur', '👁️');

  const canSubmit = contribution.trim() && (!redactionLongue ? contribution.length <= MAX_CHAR_LIMIT : true);

  return (
    <div className="redaction-container">
      {saveError && saveError.type === 'FETCH_WARNING' && (
        <div className="card bg-warning text-white mb-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⚠️</span>
            <div>
              <h4 className="font-semibold mb-1">Avertissement</h4>
              <p className="text-sm">{saveError.details}</p>
              <button className="btn btn-sm mt-2" style={{background: 'rgba(255,255,255,0.2)'}} onClick={() => { setSaveError(null); fetchCurrentContribution(); }}>🔄 Réessayer le chargement</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <h3 className="card-title">✍️ Votre contribution</h3>
        
        <div className="form-group">
          {redactionLongue ? (
            <div className="textarea-container">
              <textarea
                ref={inputRef}
                value={contribution}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="Rédigez votre contribution ici..."
                className={`form-textarea ${!displayedContribution && !contribution ? 'border-warning' : ''}`}
                rows="6"
              />
              {!displayedContribution && !contribution && (
                <div className="input-hint"><span className="hint-icon">💡</span><span className="hint-text">Commencez par écrire votre contribution</span></div>
              )}
            </div>
          ) : (
            <div className="input-container">
              <input
                ref={inputRef}
                type="text"
                value={contribution}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="Écrivez votre contribution ici..."
                className={`form-input ${!displayedContribution && !contribution ? 'border-warning' : ''}`}
                maxLength={MAX_CHAR_LIMIT}
              />
              {charCount <= 30 && <div className="character-counter">{charCount} caractères restants</div>}
              {!displayedContribution && !contribution && (
                <div className="input-hint"><span className="hint-icon">💡</span><span className="hint-text">Commencez par écrire votre contribution</span></div>
              )}
            </div>
          )}
        </div>

        {!displayedContribution ? (
          <div className="submission-actions">
            <button
              onClick={handleSendContribution}
              className={`submission-button ${canSubmit ? 'submission-button-ready' : 'submission-button-disabled'}`}
              disabled={!canSubmit}
            >
              <span className="button-icon">📤</span>
              <span className="button-text">Envoyer la contribution</span>
            </button>
          </div>
        ) : (
          <div className="contribution-submitted">
            <div className="submitted-summary">
              <div className="submitted-status"><span className="success-icon">✅</span><span className="success-text">Contribution envoyée - Vous êtes prêt !</span></div>
              <div className="submitted-preview">{displayedContribution}</div>
              <div className="contribution-actions">
                <button
                  onClick={handleCancelContribution}
                  className="cancel-button"
                >
                  <span className="button-icon">🗑️</span>
                  <span className="button-text">Annuler ma contribution</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {retryCount > 0 && (
          <div className="text-center mt-3"><p className="text-sm text-warning">⚠️ Des problèmes de connexion ont été rencontrés</p></div>
        )}
      </div>
    </div>
  );
};

export default Redaction;